import { useState } from "react";

import OxygenInput from "@8x8/oxygen-input";
import OxygenTextArea from "@8x8/oxygen-textarea";
import { IconButton } from "@8x8/oxygen-icon-button";
import CloseLargeIcon from "@8x8/oxygen-icon/novo/CloseLargeIcon";
import styled from "styled-components";

/**
 * Tests if the event indicates the enter key for the purposes of submission,
 * based on if we're using single-line or multi-line mode.
 *
 * The code for the enter key is 13, but we want to allow the user to enter multi-line text
 * without invoking the submission. A common way to do that is with Shift+Enter.
 * So this check allows us to have Shift+Enter input a newline, but not invoke the submit action
 * unless it is solely the enter key. The additional meta-keys in combination with Enter could
 * potentially invoke other actions, so we ignore those too.
 *
 * @param event Event to test
 * @param multiLine Whether we're in single-line or multi-line mode
 * @return {boolean} Whether the event is only the enter key, indicating submission
 */
const isSubmissionEvent = (event, multiLine) => {
  return (
    event.keyCode === 13 &&
    (multiLine
      ? !event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey
      : true)
  );
};

/**
 * Tests if the input is valid according to the validator function,
 * and returns true if so. If the validator function is not provided,
 * then the input is always valid.
 *
 * NOTE: The validator function is expected to operate on `event.target.value`.
 *
 * @param event Event whose text we plan to test
 * @param validator Validator function for the event's text
 * @param allowInvalidInput Override flag to consider the entry valid even if the validator fails
 * @return {boolean} If the input is considered valid to be displayed
 */
const isValidEntry = (event, validator, allowInvalidInput) => {
  if (validator) {
    if (!validator(event)) {
      return allowInvalidInput;
    }
  }
  return true;
};

/**
 * An icon button with a large X symbol, indicating to clear the input.
 *
 * @param onClick The function to call when the clear button is clicked
 * @return {JSX.Element} The clear button
 * @constructor
 */
const ClearInputButton = ({ onClick }) => {
  return (
    <IconButton onClick={onClick}>
      <CloseLargeIcon />
    </IconButton>
  );
};

/**
 * A styled version of `ClearInputButton` which is positioned at the top-right of the input.
 */
export const TopRightClearInputButton = styled(ClearInputButton)`
  display: flex;
  position: absolute;
  top: 5px;
  right: 5px;
`;

/**
 * This represents a text input component, which can be either single-line or multi-line.
 * It supports the following additional features:
 * - Submit action on enter key press
 * - Validation of input according to a custom function
 * - If the validator function indicates the input is invalid,
 * the component can optionally show it, or prevent the invalid input from being used to update the value.
 * The effect is that as long as the user types invalid input, the content does not actually change
 *
 * It is important to note that this component uses custom handlers for `onKeyDown` and `onChange`.
 *
 * @param props The properties of the input component
 * - value: The value to show in the input
 * - setValue: The function which sets the value of the input, keeping it updated as the user changes it
 * <br>It is suggested that `value` and `setValue` be the return values of a `React.useState` call
 * - submitOnEnter: Whether to invoke the submit action when the enter key is pressed
 * - multiLine: Whether the input is multi-line.
 * When `true`, the key-combo `Shift+Enter` can be used to insert newlines.
 * - onSubmit: The function to call performing the submit action.
 * If `submitOnEnter` is set, this is the function called when the enter key is pressed.
 * - onKeyDown: The function to call when a key is pressed.
 * This should only be used if special key-down handling is needed,
 * as the component has its own customized `onKeyDown` handler.
 * - onChange: The function to call when the input changes.
 * This should only be used if special handling is needed,
 * as the component has its own customized `onChange` handler.
 * - onClear: The function to call when the input is cleared; only used if `clearable` is `true`.
 * - validator: The function to validate the input.
 * The input is the event, and it is expected that the logic will operate on `event.target.value`.
 * - clearable: Whether to enable a clear button, which appears on the right side of the input when there is content.
 * If clicked, the `setValue` function is called with an empty string, followed by the `onClear` function.
 * - allowInvalidInput: Whether to allow the input to change even if the validation fails.
 * This does not apply to submitting the input, only to changing it; if the input is invalid, the submission will fail.
 * @return {JSX.Element} The input component
 * @constructor
 */
const Input = (props) => {
  const {
    value,
    setValue,
    submitOnEnter,
    multiLine,
    onSubmit,
    onKeyDown,
    onChange,
    onClear,
    validator,
    clearable = false,
    allowInvalidInput = false,
    children,
    ...others
  } = props;

  let [submissionRequest, setSubmissionRequest] = useState(false);

  // The onKeyDown handler is called first, so if we detect a submission request
  // then save that information for the onChange handler to use
  const onKeyDownHandler = (event) => {
    if (
      onSubmit &&
      isSubmissionEvent(event, multiLine) &&
      isValidEntry(event, validator, false)
    ) {
      setSubmissionRequest(true);
      onSubmit();
    } else if (onKeyDown) {
      setSubmissionRequest(false);
      onKeyDown(event);
    } else {
      setSubmissionRequest(false);
    }
  };

  const onChangeHandler = (event) => {
    if (submitOnEnter && submissionRequest) {
      // Submission request, we do not want to change anything, so skip out here
      return;
    }
    // Even though the 'event' objects between onKeyDown and onChange are different,
    // the item 'target.value' in them is the same, and it is expected that the validator
    // will be testing this value.
    if (isValidEntry(event, validator, allowInvalidInput) && setValue) {
      setValue(event.target.value);
    }
    // Run any custom onChange handler
    if (onChange) {
      onChange(event);
    }
  };

  const onClearHandler = (event) => {
    if (setValue) {
      setValue("");
    }
    // TODO: The callback cannot assume the value is empty, because the event cycle may not have completed yet
    if (onClear) {
      onClear(event);
    }
  };

  const inputProps = {
    value,
    onKeyDown: submitOnEnter ? onKeyDownHandler : onKeyDown,
    onChange: onChangeHandler,
    ...others,
  };

  return multiLine ? (
    <>
      <OxygenTextArea {...inputProps}>{children}</OxygenTextArea>
      {clearable && <TopRightClearInputButton onClick={onClearHandler} />}
    </>
  ) : (
    <OxygenInput
      {...inputProps}
      fullWidth
      iconRight={() =>
        clearable && value ? (
          <ClearInputButton onClick={onClearHandler} />
        ) : (
          // This is actually meaningful, because without it the width of the input will change
          // whenever the clear button appears or disappears; with it, things are consistent
          <></>
        )
      }
    >
      {children}
    </OxygenInput>
  );
};

export default Input;
